import React, { useEffect, useState } from "react";

import axios from "axios";

import { useNavigate, useParams } from "react-router-dom";

import toast from "react-hot-toast";

import Loader from "./UpcomingCourseLoader";

const EditUpcomingCourse = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    isActive: true,
  });

  // ============================================================
  // FETCH UPCOMING COURSE
  // ============================================================

  useEffect(() => {
    if (!id) {
      toast.error("Invalid upcoming course ID.");
      navigate("/upcoming-courses");
      return;
    }

    fetchUpcomingCourse();
  }, [id]);

  const fetchUpcomingCourse = async () => {
    try {
      setFetchingData(true);

      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/upcoming-courses/${id}`,
        headers: {},
      };

      const startTime = Date.now();

      const response = await axios.request(config);

      const courseData = response.data?.data;

      if (!courseData) {
        throw new Error("Upcoming course data not found.");
      }

      const elapsed = Date.now() - startTime;
      const remaining = Math.max(1000 - elapsed, 0);

      setFormData({
        name: courseData.name || "",
        isActive:
          typeof courseData.isActive === "boolean"
            ? courseData.isActive
            : true,
      });

      setTimeout(() => {
        setFetchingData(false);
      }, remaining);
    } catch (error) {
      console.error("Error fetching upcoming course:", error);

      toast.error(
        error.response?.data?.message ||
          "Failed to fetch upcoming course data.",
        {
          position: "top-right",
          duration: 3000,
        }
      );

      navigate("/upcoming-courses");
    } finally {
      setTimeout(() => {
        setFetchingData(false);
      }, 1000);
    }
  };

  // ============================================================
  // INPUT CHANGE
  // ============================================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // STATUS CHANGE
  // ============================================================

  const handleStatusChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      isActive: e.target.checked,
    }));
  };

  // ============================================================
  // SUBMIT
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Please enter course name.", {
        position: "top-right",
      });

      return;
    }

    if (!id) {
      toast.error("Invalid upcoming course ID.", {
        position: "top-right",
      });

      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        name: formData.name.trim(),
        isActive: formData.isActive,
      };

      const config = {
        method: "put",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/upcoming-courses/${id}`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(dataToSend),
      };

      const response = await axios.request(config);

      toast.success(
        response.data?.message ||
          "Upcoming course updated successfully!",
        {
          position: "top-right",
          duration: 2000,
        }
      );

      navigate("/upcoming-courses");
    } catch (error) {
      console.error("Error updating upcoming course:", error);

      toast.error(
        error.response?.data?.message ||
          "Failed to update upcoming course. Please try again.",
        {
          position: "top-right",
          duration: 3000,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (fetchingData) {
    return <Loader />;
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="w-full mx-auto rounded-lg shadow-md p-6">
      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-primaryTextColor mb-4 mt-6">
        Edit Upcoming Course
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ====================================================== */}
        {/* BASIC INFORMATION */}
        {/* ====================================================== */}

        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-secondaryTextColor">
            Basic Information
          </h2>

          {/* Course Name */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-secondaryTextColor mb-1"
            >
              Course Name *
            </label>

            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter upcoming course name"
              required
              disabled={loading}
              className="
                w-full
                px-3
                py-2
                border
                border-gray-300
                rounded-md
                bg-transparent
                text-primaryTextColor
                placeholder-gray-400
                focus:outline-none
                focus:ring-2
                focus:ring-secondaryColor
                disabled:opacity-60
                disabled:cursor-not-allowed
              "
            />
          </div>

          {/* Course Status */}
          <div>
            <label className="block text-sm font-medium text-secondaryTextColor mb-1">
              Course Status
            </label>

            <div className="flex items-center justify-between gap-4 border border-gray-300 rounded-md px-4 py-3">
              <div>
                <p className="text-md font-medium text-primaryTextColor">
                  {formData.isActive ? "Active" : "Inactive"}
                </p>

                <p className="text-sm text-secondaryTextColor mt-1">
                  Control whether this upcoming course is currently active or inactive
                </p>
              </div>

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleStatusChange}
                  disabled={loading}
                  className="sr-only peer"
                />

                <div
                  className="
                    w-11
                    h-6
                    bg-gray-300
                    rounded-full
                    peer
                    peer-checked:bg-secondaryColor
                    transition-colors
                    duration-200
                    relative
                    after:content-['']
                    after:absolute
                    after:top-[2px]
                    after:left-[2px]
                    after:bg-white
                    after:rounded-full
                    after:h-5
                    after:w-5
                    after:transition-transform
                    after:duration-200
                    peer-checked:after:translate-x-5
                  "
                />
              </label>
            </div>
          </div>
        </div>

 

        {/* ====================================================== */}
        {/* ACTION BUTTONS */}
        {/* ====================================================== */}

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate("/upcoming-courses")}
            disabled={loading}
            className="
              px-6
              py-3
              bg-gray-500
              text-white
              font-medium
              rounded-md
              hover:bg-gray-600
              transition-colors
              disabled:bg-gray-400
              disabled:cursor-not-allowed
            "
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="
              px-6
              py-3
              bg-primaryBtnColor
              text-white
              font-medium
              rounded-md
              hover:bg-opacity-90
              transition-opacity
              disabled:bg-gray-400
              disabled:cursor-not-allowed
            "
          >
            {loading ? "Updating Course..." : "Update Course"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditUpcomingCourse;